declare global {
  interface Window {
    inkeepBaseSettings: {
      apiKey: string;
      userProperties: {
        id: string;
      };
      onEvent: (event: {
        eventName: string;
        properties: { conversation: { id: string; messages: { content: string }[] } };
      }) => void;
    };
    inkeepDefaultChatButtonEnabled: boolean;
    inkeepConfig?: () => object;
    Inkeep: {
      ChatButton: (config: object) => {
        update: (config: object) => void;
      };
      ModalSearchAndChat: (config: object) => {
        update: (config: object) => void;
      };
      EmbeddedSearch: (target: string, config: object) => void;
    };
    initializeInkeepEmbeddedSearch: (searchQuery?: string) => void;
  }
}

interface SourceItem {
  id?: string;
  title: string | undefined;
  url: string;
  description: string | undefined;
  breadcrumbs: string[];
  type: string;
  contentType?: string;
  tag?: string;
  tabs?: string[];
}

const transformSource = (source: SourceItem) => {
  const { url, breadcrumbs } = source;
  if (!url) return source;
  try {
    const { pathname } = new URL(url);
    const urlPatterns = [
      { pattern: '/docs', tabName: 'Docs' },
      { pattern: '/blog', tabName: 'Blog' },
      { pattern: 'faqs.ably.com', tabName: 'FAQs' },
    ];

    const matchingPattern = urlPatterns.find(({ pattern }) => pathname.startsWith(pattern) || url.includes(pattern));

    if (matchingPattern) {
      const { tabName } = matchingPattern;
      return {
        ...source,
        tabs: [
          [
            tabName,
            {
              breadcrumbs: breadcrumbs[0] === tabName ? breadcrumbs.slice(1) : breadcrumbs,
            },
          ],
        ],
      };
    }
    return source;
  } catch {
    return source;
  }
};

const prefilledSearchText = () => {
  const queryParam = new URLSearchParams(window.location.search).get('q');

  return queryParam || '';
};

const searchSettings = {
  defaultQuery: prefilledSearchText(),
  tabs: ['All', 'Docs', 'Blog', 'FAQs', 'GitHub'],
};

const theme = {
  styles: [
    {
      key: 'custom-style',
      type: 'style',
      value: `css
            .ikp-ai-chat-message-toolbar {
              flex-wrap: wrap;
              justify-content: flex-end;
            }
            .ikp-ai-chat-message-tool-actions {
              width: 100%;
            }
            .ikp-ai-chat-message-tool-action {
              background: #03020d;
              color: white;
              height: 36px;
              font-size: 15px;
              border: none;
              margin: 0px auto;
              margin-bottom: 8px;
              padding: 12px 24px;
              height: auto;
              gap: 12px;
            }
            .ikp-ai-chat-message-tool-action > .ikp-icon {
              font-size: 18px;
            }
            .ikp-ai-chat-message-tool-action:hover:not([disabled]) {
              background: #2c3344;
              color: white;
            }
          `,
    },
  ],
};

const baseSettings = { ...window.inkeepBaseSettings, transformSource, theme };

interface ConversationEvent {
  properties: {
    conversation: {
      id: string;
      messages: { content: string }[];
    };
  };
}

const sendConversationData = (event: ConversationEvent) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  if (!csrfToken) {
    return;
  }

  const payload = {
    conversation: {
      conversation_id: event.properties.conversation.id,
      message: event.properties.conversation.messages.at(-1)?.content,
      role: 'user',
      source: 'inkeep',
    },
  };

  fetch('/api/conversations', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(payload),
  });
};

baseSettings.onEvent = (event) => {
  if (event.eventName === 'user_message_submitted') {
    sendConversationData(event);
  }
};

const config = () => ({
  baseSettings,
  label: 'Ask Ably',
  aiChatSettings: {
    organizationDisplayName: 'Ably',
    aiAssistantAvatar:
      'https://storage.googleapis.com/organization-image-assets/ably-botAvatarSrcUrl-1744182878600.png',
    getHelpOptions: [
      {
        name: 'Speak to sales',
        action: {
          type: 'open_link',
          url: '/contact',
        },
        isPinnedToToolbar: true,
      },
      {
        name: 'Get support',
        action: {
          type: 'open_link',
          url: '/support',
        },
        isPinnedToToolbar: true,
      },
    ],
    exampleQuestions: ['What is a channel?', 'How do I authenticate with Ably?', 'How to manage user status?'],
    shouldShowCopyChatButton: true,
  },
  searchSettings,
});

// Expose config function globally so other parts of the app can use it
window.inkeepConfig = config;

if (window.inkeepDefaultChatButtonEnabled) {
  document.addEventListener('DOMContentLoaded', () => {
    window.Inkeep.ChatButton({
      ...config(),
    });
  });
}

const initializeEmbeddedSearch = (searchQuery?: string) => {
  const embeddedConfig = {
    ...config(),
    shouldAutoFocusInput: true,
  };

  if (searchQuery) {
    embeddedConfig.searchSettings = {
      ...embeddedConfig.searchSettings,
      defaultQuery: searchQuery,
    };
  }

  return window.Inkeep.EmbeddedSearch('#ikp-embedded-search-target', embeddedConfig);
};

window.initializeInkeepEmbeddedSearch = initializeEmbeddedSearch;

let searchWidget: ReturnType<typeof window.Inkeep.ModalSearchAndChat>;

const handleSearchChange = (isOpen: boolean) => {
  searchWidget.update({ modalSettings: { isOpen } });
};

const modalSettings = {
  modalSettings: {
    defaultView: 'SEARCH',
    onOpenChange: handleSearchChange,
  },
};

document.addEventListener('DOMContentLoaded', () => {
  searchWidget = window.Inkeep.ModalSearchAndChat({ ...config(), ...modalSettings });
  const searchButton = { element: document.querySelector("[data-id='inkeep-search']"), event: 'click' };
  const searchInput = { element: document.querySelector("[data-id='inkeep-search-input']"), event: 'focus' };

  [searchButton, searchInput].forEach((input) => {
    input.element?.addEventListener(input.event, () => {
      handleSearchChange(true);
    });
  });
});

export {};
